import type { LicenseFitResult, LicenseFitRow, FitVerdict } from './LicenseFitTypes';

/** One (user, connection, model) usage cell from the analytics aggregate. */
export interface LicenseFitUsageCell {
    userId: string;
    connectionId: string;
    model: string;
    allocatedCostUsd: number;
    totalTokens: number;
}

/** An active seat assignment (user holds a managed seat on a seat-licensed connection). */
export interface LicenseFitSeat {
    userId: string;
    connectionId: string;
}

/** Provider-connection metadata. Seat-licensed iff the connection has a license pool. */
export interface LicenseFitConnection {
    provider: string;
    seatLicensed: boolean;
    /** Monthly cost of one seat on this connection; null when unknown (no spend or no assigned seats). */
    perSeatMonthly: number | null;
}

export interface LicenseFitInput {
    usage: LicenseFitUsageCell[];
    seats: LicenseFitSeat[];
    connections: Map<string, LicenseFitConnection>;
    /** Empirical $ per 1,000,000 tokens, per model. */
    rateByModel: Map<string, number>;
    userLabel: (userId: string) => string;
    currency: string;
    /** Cheapest known per-seat monthly price across seat-licensed connections; null when the org has no priced seats. */
    cheapestSeatMonthly: number | null;
    /** Working-day run-rate multiplier that projects month-to-date usage to a full month (≥ 1). */
    projectionRatio: number;
    /** Monthly delta below which a row is judged neutral (noise floor). */
    minDelta: number;
    /** Normalized user id → this month's platform (classifier) overhead cost attributed to that caller. */
    platformOverheadByUser: Map<string, number>;
}

/** Case-insensitive user key — matches how shadow-seat detection joins telemetry to assignments. */
export function normalizeUserId(userId: string): string {
    return userId.trim().toLowerCase();
}

interface UsageBucket {
    userId: string;
    connectionId: string;
    tokens: number;
    cost: number;
    byModel: Map<string, number>;
}

/** Fraction of a bucket's tokens that map to a known empirical rate — below this the metered-equivalent is flagged low-confidence. */
const MIN_RATE_COVERAGE = 0.6;

function meteredEquivalent(bucket: UsageBucket, rateByModel: Map<string, number>): { equivUsd: number; coveredTokens: number } {
    let equivUsd = 0;
    let coveredTokens = 0;
    for (const [model, tokens] of bucket.byModel) {
        const rate = rateByModel.get(model);
        if (rate === undefined) continue;
        equivUsd += (tokens / 1_000_000) * rate;
        coveredTokens += tokens;
    }
    return { equivUsd, coveredTokens };
}

/**
 * Compares each user's current footing against the alternative and produces per-user rows plus org totals.
 * Seat rows come from active priced assignments (so zero-usage wasted seats are included). Usage rows cover
 * every remaining (user, connection) usage not matched to a priced managed seat — real metered spend on
 * usage-only platforms AND seat-platform usage by users with no (or unpriced) managed seat (the
 * should-license / shadow-seat case), benchmarked against that platform's own seat price when known.
 * Pure — all I/O and filtering happen in the ViewModel.
 *
 * Platform (classifier) overhead folds into each user's displayed Actual, Forecast, and Alternative cost
 * (once, on their top row) so those figures reflect the user's true total cost. It is footing-independent —
 * the classifier runs on the user's data whichever way they are billed — so it is added to BOTH sides of the
 * footing comparison and cancels in `deltaUsd`, leaving the verdict unchanged. `platformOverheadUsd` carries
 * the folded amount for the (optional) breakout column and the verdict tooltip.
 */
export function computeLicenseFit(input: LicenseFitInput): LicenseFitResult {
    const { usage, seats, connections, rateByModel, userLabel, currency, cheapestSeatMonthly, projectionRatio, minDelta, platformOverheadByUser } = input;

    const buckets = new Map<string, UsageBucket>();
    const bucketKey = (userId: string, connectionId: string): string => `${normalizeUserId(userId)}:${connectionId}`;
    for (const cell of usage) {
        const key = bucketKey(cell.userId, cell.connectionId);
        let bucket = buckets.get(key);
        if (bucket === undefined) {
            bucket = { userId: cell.userId, connectionId: cell.connectionId, tokens: 0, cost: 0, byModel: new Map() };
            buckets.set(key, bucket);
        }
        bucket.tokens += cell.totalTokens;
        bucket.cost += cell.allocatedCostUsd;
        if (cell.model.length > 0 && cell.model !== '—') {
            bucket.byModel.set(cell.model, (bucket.byModel.get(cell.model) ?? 0) + cell.totalTokens);
        }
    }

    const rows: LicenseFitRow[] = [];
    const seatedKeys = new Set<string>();

    for (const seat of seats) {
        const connection = connections.get(seat.connectionId);
        if (connection === undefined || !connection.seatLicensed || connection.perSeatMonthly === null) continue;
        const key = bucketKey(seat.userId, seat.connectionId);
        if (seatedKeys.has(key)) continue;
        seatedKeys.add(key);

        const bucket = buckets.get(key);
        const tokens = bucket?.tokens ?? 0;
        const { equivUsd, coveredTokens } = bucket === undefined ? { equivUsd: 0, coveredTokens: 0 } : meteredEquivalent(bucket, rateByModel);
        const forecastUsd = equivUsd * projectionRatio;
        const actualUsd = connection.perSeatMonthly;
        const deltaUsd = actualUsd - forecastUsd;
        const verdict: FitVerdict = deltaUsd > minDelta ? 'overpaying' : deltaUsd < -minDelta ? 'good-value' : 'api-optimal';
        rows.push({
            key,
            userId: seat.userId,
            userLabel: userLabel(seat.userId),
            provider: connection.provider,
            footing: 'seat',
            actualUsd,
            alternativeUsd: equivUsd,
            forecastUsd,
            deltaUsd,
            verdict,
            tokens,
            lowConfidence: tokens > 0 && coveredTokens / tokens < MIN_RATE_COVERAGE,
            platformOverheadUsd: 0,
        });
    }

    for (const [key, bucket] of buckets) {
        if (seatedKeys.has(key)) continue;
        const connection = connections.get(bucket.connectionId);
        if (connection === undefined) continue;
        if (bucket.cost <= 0) continue;

        const seatBenchmark = connection.seatLicensed && connection.perSeatMonthly !== null ? connection.perSeatMonthly : cheapestSeatMonthly;
        const actualUsd = bucket.cost;
        const forecastUsd = actualUsd * projectionRatio;
        const userId = bucket.userId;
        let deltaUsd: number | null;
        let verdict: FitVerdict;
        if (seatBenchmark === null) {
            deltaUsd = null;
            verdict = 'no-alternative';
        } else {
            deltaUsd = forecastUsd - seatBenchmark;
            verdict = deltaUsd > minDelta ? 'should-license' : 'api-optimal';
        }
        rows.push({
            key,
            userId,
            userLabel: userLabel(userId),
            provider: connection.provider,
            footing: 'metered',
            actualUsd,
            alternativeUsd: seatBenchmark,
            forecastUsd,
            deltaUsd,
            verdict,
            tokens: bucket.tokens,
            lowConfidence: false,
            platformOverheadUsd: 0,
        });
    }

    rows.sort((a, b) => (b.deltaUsd ?? Number.NEGATIVE_INFINITY) - (a.deltaUsd ?? Number.NEGATIVE_INFINITY));

    const overheadAttached = new Set<string>();
    for (const row of rows) {
        const nu = normalizeUserId(row.userId);
        const overhead = platformOverheadByUser.get(nu) ?? 0;
        if (overhead <= 0 || overheadAttached.has(nu)) continue;
        overheadAttached.add(nu);
        row.platformOverheadUsd = overhead;
        row.actualUsd += overhead;
        row.forecastUsd += overhead;
        if (row.alternativeUsd !== null) row.alternativeUsd += overhead;
    }

    let wastedSeatUsd = 0;
    let licenseValueUsd = 0;
    let apiToSeatUsd = 0;
    let reclaimCandidates = 0;
    for (const row of rows) {
        if (row.verdict === 'overpaying' && row.deltaUsd !== null) {
            wastedSeatUsd += row.deltaUsd;
            reclaimCandidates += 1;
        } else if (row.verdict === 'good-value' && row.deltaUsd !== null) {
            licenseValueUsd += -row.deltaUsd;
        } else if (row.verdict === 'should-license' && row.deltaUsd !== null) {
            apiToSeatUsd += row.deltaUsd;
        }
    }

    return {
        rows,
        summary: {
            wastedSeatUsd,
            licenseValueUsd,
            apiToSeatUsd,
            netOpportunityUsd: wastedSeatUsd + apiToSeatUsd,
            reclaimCandidates,
            currency,
            platformOverheadUsd: [...platformOverheadByUser.values()].reduce((sum, value) => sum + value, 0),
            lowCoverage: rateByModel.size === 0,
        },
    };
}
